import { Injectable } from '@angular/core';

export interface AppTheme {
  key: string;
  name: string;
  description: string;
  colors: string[];
  preview: {
    bg: string;
    panel: string;
    line: string;
    primary: string;
    accent: string;
    sidebar: string;
    text: string;
  };
}

const STORAGE_KEY = 'procurement-ui-theme';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  readonly themes: AppTheme[] = [
    {
      key: 'civic',
      name: 'Civic Blue',
      description: 'Clean government office theme with blue, teal, and amber accents.',
      colors: ['#1e3a8a', '#0f766e', '#f59e0b'],
      preview: {
        bg: '#f4f7fb',
        panel: '#ffffff',
        line: '#d7e1ed',
        primary: '#2563eb',
        accent: '#0f766e',
        sidebar: 'linear-gradient(160deg, #13233f 0%, #1e3a8a 52%, #0f766e 100%)',
        text: '#172033'
      }
    },
    {
      key: 'classic',
      name: 'OFS Classic',
      description: 'Original fire service palette with warm red and gold tones.',
      colors: ['#a61d17', '#d92d20', '#f4b000'],
      preview: {
        bg: '#f7f5f0',
        panel: '#ffffff',
        line: '#ead9bf',
        primary: '#d92d20',
        accent: '#f4b000',
        sidebar: 'linear-gradient(160deg, #3a1612 0%, #a61d17 52%, #f4b000 100%)',
        text: '#241817'
      }
    },
    {
      key: 'forest',
      name: 'Forest Ledger',
      description: 'Calm green procurement desk theme for long register work.',
      colors: ['#14532d', '#15803d', '#ca8a04'],
      preview: {
        bg: '#f4f7ef',
        panel: '#ffffff',
        line: '#d8e5c7',
        primary: '#15803d',
        accent: '#ca8a04',
        sidebar: 'linear-gradient(160deg, #102a1c 0%, #14532d 52%, #ca8a04 100%)',
        text: '#172315'
      }
    },
    {
      key: 'graphite',
      name: 'Graphite',
      description: 'Neutral charcoal theme with focused blue highlights.',
      colors: ['#111827', '#2563eb', '#f97316'],
      preview: {
        bg: '#f5f6f8',
        panel: '#ffffff',
        line: '#d9dee7',
        primary: '#2563eb',
        accent: '#f97316',
        sidebar: 'linear-gradient(160deg, #111827 0%, #1f2937 56%, #2563eb 100%)',
        text: '#151922'
      }
    }
  ];

  private activeThemeKey = '';

  constructor() {
    const savedTheme = localStorage.getItem(STORAGE_KEY);
    this.applyTheme(savedTheme || 'civic');
  }

  get activeTheme(): AppTheme {
    return this.themes.find((theme) => theme.key === this.activeThemeKey) || this.themes[0];
  }

  applyTheme(themeKey: string): void {
    const nextTheme = this.themes.find((theme) => theme.key === themeKey) || this.themes[0];
    this.activeThemeKey = nextTheme.key;
    document.body.dataset['theme'] = nextTheme.key;
    localStorage.setItem(STORAGE_KEY, nextTheme.key);
  }

  isActive(themeKey: string): boolean {
    return this.activeThemeKey === themeKey;
  }
}
